import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import TagInput from '@/components/TagInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Busy, Field, Panel } from '@/components/kit';
import { PickSelect, SwitchRow } from '@/components/inputs';
import { CONTACT_METHOD_VALUES, CUSTOMER_TAGS } from '@/shared/constants';
import { contactPlaceholder, newCode } from '@/shared/pricing';
import { fromLocalInput, toArray, toLocalInput } from '@/shared/data';

export function customerToForm(c) {
  return {
    Name: c?.Name ?? '',
    email_c: c?.email_c ?? '',
    contact_method_c: c?.contact_method_c ?? 'Discord',
    contact_handle_c: c?.contact_handle_c ?? '',
    referral_code_c: c?.referral_code_c ?? '',
    referrals_earned_c: String(c?.referrals_earned_c ?? 0),
    marketing_opt_in_c: !!c?.marketing_opt_in_c,
    expiry_reminders_c: c ? !!c.expiry_reminders_c : true,
    unsubscribed_at_c: toLocalInput(c?.unsubscribed_at_c),
    tags_c: toArray(c?.tags_c).join(','),
    notes_c: c?.notes_c ?? '',
  };
}

export function blankCustomer() {
  return { ...customerToForm(null), referral_code_c: newCode('REF-', 6) };
}

function toPayload(form) {
  return {
    Name: form.Name.trim(),
    email_c: form.email_c.trim().toLowerCase(),
    contact_method_c: form.contact_method_c || null,
    contact_handle_c: form.contact_handle_c.trim(),
    referral_code_c: form.referral_code_c.trim().toUpperCase(),
    referrals_earned_c: Math.max(0, Math.round(Number(form.referrals_earned_c || 0))) || 0,
    marketing_opt_in_c: !!form.marketing_opt_in_c,
    expiry_reminders_c: !!form.expiry_reminders_c,
    unsubscribed_at_c: fromLocalInput(form.unsubscribed_at_c),
    tags_c: toArray(form.tags_c).join(','),
    notes_c: form.notes_c.trim(),
  };
}

/** Full customer form for the New and Edit pages. `onSubmit` receives the write payload. */
export default function CustomerForm({ initial, onSubmit, saving, submitLabel = 'Save customer', busyLabel = 'Saving…', cancelTo }) {
  const [form, setForm] = useState(initial);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  function submit(e) {
    e.preventDefault();
    if (!form.Name.trim()) return toast.error('Add the customer name');
    if (!/^\S+@\S+\.\S+$/.test(form.email_c.trim())) return toast.error('Add a valid email address');
    return onSubmit(toPayload(form));
  }

  return (
    <form onSubmit={submit}>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Who they are" icon="User" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="c-name">
            <Input id="c-name" value={form.Name} onChange={text('Name')} placeholder="Maya Okafor" />
          </Field>
          <Field label="Email" htmlFor="c-email">
            <Input id="c-email" type="email" value={form.email_c} onChange={text('email_c')} placeholder="maya@example.com" />
          </Field>
          <Field label="Contact method" htmlFor="c-method">
            <PickSelect
              id="c-method"
              value={form.contact_method_c}
              onChange={set('contact_method_c')}
              values={CONTACT_METHOD_VALUES}
              placeholder="Discord, WhatsApp or Telegram"
            />
          </Field>
          <Field label="Handle" htmlFor="c-handle">
            <Input
              id="c-handle"
              value={form.contact_handle_c}
              onChange={text('contact_handle_c')}
              placeholder={contactPlaceholder(form.contact_method_c)}
            />
          </Field>
          <Field label="Referral code" htmlFor="c-ref" className="sm:col-span-2" hint="Friends who use it get money off. Unique per customer.">
            <div className="flex gap-2">
              <Input
                id="c-ref"
                value={form.referral_code_c}
                onChange={text('referral_code_c')}
                placeholder="REF-7Q2MKD"
                className="font-mono uppercase"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => set('referral_code_c')(newCode('REF-', 6))}
                aria-label="Generate a new referral code"
              >
                <ApperIcon name="Shuffle" size={16} />
              </Button>
            </div>
          </Field>
          <Field
            label="Referrals earned"
            htmlFor="c-referrals"
            className="sm:col-span-2"
            hint="Goes up by itself when a friend's order is activated with their code. Adjust it for referrals made outside checkout."
          >
            <Input
              id="c-referrals"
              type="number"
              min="0"
              step="1"
              value={form.referrals_earned_c}
              onChange={text('referrals_earned_c')}
              placeholder="0"
              className="max-w-32 tabular-nums"
            />
          </Field>
        </Panel>

        <Panel title="Email preferences" icon="MailCheck" bodyClassName="space-y-3 py-4">
          <SwitchRow
            id="c-marketing"
            label="Promotions and offers"
            hint="Receives Promo broadcasts."
            checked={form.marketing_opt_in_c}
            onChange={set('marketing_opt_in_c')}
          />
          <SwitchRow
            id="c-expiry"
            label="Expiry reminders"
            hint="A heads-up before a line runs out."
            checked={form.expiry_reminders_c}
            onChange={set('expiry_reminders_c')}
          />
          <Field label="Unsubscribed on" htmlFor="c-unsub" hint="Set when they opt out of everything. Leave empty otherwise.">
            <Input
              id="c-unsub"
              type="datetime-local"
              value={form.unsubscribed_at_c}
              onChange={text('unsubscribed_at_c')}
              placeholder="2026-11-02T20:00"
            />
          </Field>
        </Panel>

        <Panel title="Tags and notes" icon="Tags" className="lg:col-span-2" bodyClassName="space-y-4 py-4">
          <Field label="Tags" htmlFor="c-tags">
            <TagInput
              value={form.tags_c}
              onChange={set('tags_c')}
              availableTags={CUSTOMER_TAGS}
              placeholder="VIP, Late payer, Referrer…"
            />
          </Field>
          <Field label="Notes" htmlFor="c-notes">
            <Textarea
              id="c-notes"
              rows={4}
              value={form.notes_c}
              onChange={text('notes_c')}
              placeholder="Prefers evening contact, two TVs in the house…"
            />
          </Field>
        </Panel>
      </div>

      <div className="sticky bottom-0 z-10 mt-6 flex justify-end gap-2 border-t border-border bg-background/95 py-3 backdrop-blur">
        <Button type="button" variant="outline" asChild>
          <Link to={cancelTo}>Cancel</Link>
        </Button>
        <Button type="submit" disabled={saving}>
          <Busy busy={saving} label={submitLabel} busyLabel={busyLabel} />
        </Button>
      </div>
    </form>
  );
}
